import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import CommonFields from "../../common/CommonField";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
} from "@carbon/react";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // Debug logs
  console.log('🔍 Amenities (Step 4) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
  });

  
  // Field Map
  const FIELD_MAP = useMemo(() => ({
    powerBackup: "com_power_backup",
    lift: "com_lift",
    parking: "com_parking",
    parkingSlots: "com_parking_slots",
    washrooms: "com_sale_washrooms",
    waterStorage: "com_water_storage",
    security: "com_security",
    wifi: "wifi",
    propertyCondition: "com_property_condition",
    currentBusiness: "com_sale_current_business_running",
  }), []);

  // State
  const [form, setForm] = useState({
    powerBackup: "",
    lift: "",
    parking: "",
    parkingSlots: "",
    washrooms: "",
    waterStorage: "",
    security: "",
    wifi: "",
    propertyCondition: "",
    currentBusiness: "",
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Get options from attributeValues
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // Check if parking type requires slots
  const showParkingSlots = useMemo(() => {
    if (!form.parking) return false;
    
    const selectedParking = getOptions("parking").find(
      item => String(item.id) === String(form.parking)
    );
    
    return selectedParking && (
      selectedParking.label.toLowerCase().includes("reserved") || 
      selectedParking.label.toLowerCase().includes("public and reserved")
    );
  }, [form.parking, getOptions]);

  // Field updates
  const updateField = useCallback((key, value) => {
    console.log(`🔄 Updating field ${key} to:`, value);
    setForm((prev) => ({ ...prev, [key]: value }));
    // Mark as touched when updated
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // Handle blur
  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Load initial data
  useEffect(() => {
    console.log('💧 Amenities hydration triggered with data:', data);

    if (!data || Object.keys(data).length === 0) {
      console.log('💧 No data available for hydration');
      return;
    }

    const amenitiesData = data.amenities || {};
    
    console.log('💧 Checking amenities data:', amenitiesData);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = amenitiesData[apiKey];
      
      if (raw == null) {
        console.log(`  - ${field} (${apiKey}): No data found in amenities`);
        return;
      }
      
      console.log("DEBUG FIELD:", field, {
        apiValue: raw,
        options: getOptions(field),
      });
      
      console.log(`  - ${field} (${apiKey}): raw value =`, raw, 'type:', typeof raw);

if (field === "similarPropertiesAvailable") {
  incoming[field] =
    raw === true ||
    raw === "true" ||
    raw === 1 ||
    raw === "1" ||
    raw === "Yes";
} else if (
  field === "powerBackup" ||
  field === "lift" ||
  field === "parking" ||
  field === "washrooms" ||
  field === "waterStorage" ||
  field === "security" ||
  field === "wifi" ||
  field === "propertyCondition" ||
  field === "currentBusiness"
) {
  const options = getOptions(field);

  const matchedOption = options.find(
    (option) =>
      String(option.id) === String(raw) ||
      String(option.label).trim().toLowerCase() ===
        String(raw).trim().toLowerCase()
  );

  incoming[field] = matchedOption
    ? String(matchedOption.id)
    : String(raw);
} else {
  incoming[field] = String(raw);
}
    });

    console.log('💧 Amenities incoming form data:', incoming);
    
    if (Object.keys(incoming).length > 0) {
      setForm((prev) => {
        const newForm = { ...prev, ...incoming };
        console.log('💧 Amenities new form state:', newForm);
        return newForm;
      });
      
      // Mark all hydrated fields as touched
      const hydratedTouched = {};
      Object.keys(incoming).forEach(key => {
        hydratedTouched[key] = true;
      });
      setTouched(prev => ({ ...prev, ...hydratedTouched }));
    }
  }, [data, FIELD_MAP, getOptions]);

  // Validation - Only washrooms is required now
  const getFieldError = useCallback((field) => {
    const v = form[field];

    // Only validate washrooms
    if (field === "washrooms" && !v) {
      return "Washroom(s) is required";
    }
    
    // Conditional validation for parkingSlots
    if (field === "parkingSlots" && showParkingSlots) {
      if (!v || v === "") {
        return "Number of parking slots is required";
      }
      if (Number(v) <= 0) {
        return "Parking slots must be greater than 0";
      }
    }

    return null;
  }, [form, showParkingSlots]);

  // Check if form has errors (for save validation)
  const validateForm = useCallback(() => {
    const errors = [];
    
    // Only washrooms is required now
    if (!form.washrooms) errors.push("Washroom(s) is required");
    
    // Check parking slots if required
    if (showParkingSlots) {
      if (!form.parkingSlots || form.parkingSlots === "") {
        errors.push("Number of parking slots is required");
      } else if (Number(form.parkingSlots) <= 0) {
        errors.push("Parking slots must be greater than 0");
      }
    }
    
    return errors;
  }, [form, showParkingSlots]);

  // Mark all fields as touched for validation
  const markAllTouched = useCallback(() => {
    const allTouched = {};
    Object.keys(form).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(prev => ({ ...prev, ...allTouched }));
  }, [form]);

  // Use Common Fields with proper props
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // Save handler
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Amenities handleSave:', { 
      navigateNext, 
      formData: form 
    });

    // Mark all fields as touched to show validation errors
    markAllTouched();

    // Validate form
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(". "));
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 4...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "parkingSlots" && !showParkingSlots) continue;

        if (field === "similarPropertiesAvailable") {
          payload[apiKey] = v ? "Yes" : "No";
        }
        else if (field === "parkingSlots") {
          payload[apiKey] = Number(v);
        }
else if (
  [
    "powerBackup",
    "lift",
    "parking",
    "washrooms",
    "waterStorage",
    "security",
    "wifi",
    "propertyCondition",
    "currentBusiness",
  ].includes(field)
) {
  const options = getOptions(field);

  const selectedOption = options.find(
    (option) => String(option.id) === String(v)
  );

  payload[apiKey] = selectedOption?.label || v;
}
else {
  payload[apiKey] = v;
}
      }

      console.log('💾 Saving amenities:', payload);
      await saveSectionAttributes(uuid, payload, form, "amenities");
      

      setSuccess("Amenities saved successfully!");
      
      onSaveSuccess?.(uuid, form, currentStep);
      onComplete?.(form);

      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    validateForm,
    markAllTouched,
    propertyUuid,
    createProperty,
    currentStep,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext,
    showParkingSlots
  ]);

  // Button handlers
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    handleSave(true);
  }, [handleSave]);

  // Render UI
  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          timeout={3000}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* POWER BACKUP & LIFT */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("powerBackup", "Power Backup", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("lift", "Lift", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* PARKING & WASHROOMS */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("parking", "Parking", false)}
                    
                    {/* Conditional Parking Slots Field */}
                    {showParkingSlots && (
                      renderNumberField("parkingSlots", "Parking Slots", "Enter number", true)
                    )}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("washrooms", "Washroom(s)", true)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* WATER STORAGE & SECURITY */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("waterStorage", "Water Storage Facility", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("security", "Security", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* WIFI & PROPERTY CONDITION */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("wifi", "WiFi", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("propertyCondition", "Current Property Condition", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* CURRENT BUSINESS & SIMILAR PROPERTIES */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("currentBusiness", "What Business is Currently Running?", false)}
                  </Column>
                  
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;